import {
  DispatchOrderWizardStep,
  Movement,
  Order,
  OrderBatchDispatchPayload,
  OrderDispatchDraftPayload,
  OrderDraftArticleRow,
  MovementAdditionalItem,
  Article,
} from "types";
import { MOVEMENT_TYPES } from "constants/index";
import { buildOrderArticleLineData } from "./orderArticleLineData";

export const buildOrderDraftRowsFromOrder = (
  order: Order,
): OrderDraftArticleRow[] =>
  (order.articles ?? []).map((article) => ({
    localId: Number(article.id),
    movement_article_id: Number(article.id),
    article_id: Number(article.article_id),
    article_location_id:
      article.article_locations_id ?? article.article_locations?.id ?? null,
    location_id:
      article.location_id ?? article.article_locations?.location_id ?? null,
    location_code: article.article_locations?.location?.code ?? null,
    sub_location_code: null,
    amount: Number(article.amount ?? 0),
    amount_per_container: Number(article.amount_per_container ?? 0),
    lote: article.lote ?? "",
    oc: article.oc ?? "",
    fecha_ingreso:
      article.fecha_ingreso ?? article.article_locations?.fecha_ingreso ?? null,
    fecha_vencimiento: article.fecha_vencimiento ?? null,
    stock_unit_selection: article.stock_unit_selection,
    observation_preview_units: article.observation_preview_units,
  }));

export const buildDispatchOrderWizardStep = (
  order: Order,
): DispatchOrderWizardStep => ({
  order_id: Number(order.id),
  date: new Date(),
  associated_client_id: order.movement_order?.associated_client_id ?? null,
  original_associated_client_id: order.movement_order?.associated_client_id ?? null,
  client_document: order.client_document ?? "",
  numero_remito: order.numero_remito ?? "",
  pallets:
    order.pallets != null && Number.isFinite(Number(order.pallets))
      ? Number(order.pallets)
      : null,
  weight:
    order.weight != null && Number.isFinite(Number(order.weight))
      ? Number(order.weight)
      : null,
  observation: order.observation ?? "",
  articles: buildOrderDraftRowsFromOrder(order),
  additional_items: (order.additional_items ?? []).map((additionalItem) => {
    const additionalLineClientId =
      (additionalItem as MovementAdditionalItem & { line_client_id?: number | null })
        .line_client_id ??
      additionalItem.client_id ??
      additionalItem.additional_item?.client_id ??
      null;

    const lineClientId =
      additionalLineClientId != null &&
      Number(additionalLineClientId) !== Number(order.client_id)
        ? Number(additionalLineClientId)
        : 0;

    return {
      ...additionalItem,
      line_client_id: lineClientId,
      line_client_name: lineClientId > 0 ? "" : "Seleccionar línea",
      price: additionalItem.price ?? additionalItem.additional_item?.price ?? 0,
    };
  }),
});

export const calculateDispatchOrderStepTotals = ({
  step,
  articles,
}: {
  step: DispatchOrderWizardStep;
  articles: Article[];
}) =>
  step.articles.reduce(
    (acc, row) => {
      const article = articles.find(
        (currentArticle) => currentArticle.id === row.article_id,
      );

      acc.totalPallets += Number(row.amount) || 0;
      acc.totalUnits +=
        (Number(row.amount) || 0) * (Number(row.amount_per_container) || 0);
      acc.totalWeight +=
        (Number(row.amount) || 0) * Number(article?.weight ?? 0);

      return acc;
    },
    {
      totalPallets: 0,
      totalUnits: 0,
      totalWeight: 0,
    },
  );

export const buildDispatchMovementForAdditionalItems = ({
  order,
  step,
}: {
  order: Order;
  step: DispatchOrderWizardStep;
}): Movement => ({
  movement_type_id: MOVEMENT_TYPES.DISCHARGE.id,
  date: step.date?.toISOString() ?? "",
  client_id: order.client_id,
  client: order.client,
  numero_remito: step.numero_remito,
  vehicle: order.vehicle ?? null,
  associated_client: order.associated_client ?? "",
  client_document: step.client_document,
  pallets: step.pallets ?? 0,
  observation: step.observation,
  created_by: order.created_by,
  updated_by: order.updated_by,
  is_billable: order.is_billable,
  store_id: order.store_id ?? null,
  weight: step.weight ?? 0,
  articles: [],
  additional_items: step.additional_items,
});

export const buildOrderDispatchDraftPayload = (
  step: DispatchOrderWizardStep,
): OrderDispatchDraftPayload => {
  const payload: OrderDispatchDraftPayload = {
    order_id: step.order_id,
    date: step.date?.toISOString() ?? "",
    client_document: step.client_document.trim(),
    numero_remito: step.numero_remito.trim(),
    pallets: step.pallets,
    weight: step.weight,
    observation: step.observation.trim() || null,
    articles: step.articles.map((article) => ({
      movement_article_id: Number(article.movement_article_id),
      article_id: Number(article.article_id),
      article_location_id: article.article_location_id ?? null,
      location_id: article.location_id ?? null,
      amount: Number(article.amount),
      amount_per_container: Number(article.amount_per_container),
      lote: article.lote || null,
      oc: article.oc || null,
      fecha_ingreso: article.fecha_ingreso ?? null,
      fecha_vencimiento: article.fecha_vencimiento ?? null,
      line_data: buildOrderArticleLineData(article),
    })),
    additional_items: step.additional_items.map((additionalItem) => ({
      additional_item_id: Number(additionalItem.additional_item_id),
      amount: Number(additionalItem.amount ?? 0),
      price:
        additionalItem.price != null
          ? Number(additionalItem.price)
          : undefined,
      line_client_id:
        additionalItem.line_client_id != null
          ? Number(additionalItem.line_client_id)
          : undefined,
    })),
  };

  if (step.associated_client_id !== step.original_associated_client_id) {
    payload.associated_client_id = step.associated_client_id;
  }

  return payload;
};

export const buildOrderDispatchBatchPayload = (
  steps: DispatchOrderWizardStep[],
): OrderBatchDispatchPayload => ({
  ids: steps.map((step) => Number(step.order_id)),
  drafts: steps.map(buildOrderDispatchDraftPayload),
});

export const cloneMovementAdditionalItems = (
  additionalItems: MovementAdditionalItem[],
): MovementAdditionalItem[] =>
  additionalItems.map((additionalItem) => ({
    ...additionalItem,
  }));
